import React from 'react';

import Form from '@Components/form';

import { IFeedbackProps } from './feedback.definition';
import SectionHeading from '@Components/sectionHeading';
import LoadingSpinner from '@Components/loadingSpinner';
import FeltTypeTextarea from '@Components/felttypetextarea';

/**
 * Feedback component for collecting user feedback and displaying form or success message.
 *
 * @param {IFeedbackProps} props - Props for the Feedback component
 * @returns {JSX.Element} Feedback UI
 */
export const Feedback = (props: IFeedbackProps) => {
	/**
	 * Renders the initial question asking if the page was helpful.
	 *
	 * @returns {JSX.Element} Question UI
	 */
	const renderQuestion = () => {
		const {
			showForm,
			feedback: {
				title,
				buttonYes,
				buttonNo
			},
			setOnSuccess,
			setShowForm
		} = props;

		if (showForm) {
			return null;
		}

		return (
			<div className="Feedback-Question">
				<SectionHeading
					className="Feedback-Heading"
					headline={ title }
				/>
				<div className="Feedback-Buttons">
					<button
						className="square-button button-type-1"
						id="feedbackknap_ja"
						onClick={ () => {
							setShowForm(true);
							setOnSuccess(true);
						} }
					>
						{ buttonYes }
					</button>
					<button
						className="square-button button-type-1"
						id="feedbackknap_nej"
						onClick={ () => setShowForm(true) }
					>
						{ buttonNo }
					</button>
				</div>
			</div>
		);
	};

	/**
	 * Renders the feedback form if showForm is true.
	 *
	 * @returns {JSX.Element|null} Feedback form UI or null
	 */
	const renderForm = () => {
		const {
			showForm,
			fields,
			isLoading,
			feedback: {
				formTitle,
				textareaMax,
				formSendButtonText
			},
			onSuccess,
			onSubmit,
			onChange
		} = props;

		if (!showForm || onSuccess) {
			return null;
		}

		return (
			<Form className="Feedback-Form" onSubmit={ onSubmit }>
				<SectionHeading
					className="Feedback-Heading"
					headline={ formTitle }
				/>
				<LoadingSpinner isLoading={ isLoading } />
				{ !isLoading && (
					<>
						<FeltTypeTextarea
							required
							id="feedback"
							name="feedback"
							maxLength={ textareaMax }
							onChange={ (val) => onChange('feedback', val) }
						/>
						<div>
							<span>
								{ `${fields.feedback.length}/${textareaMax}` }
							</span>
						</div>
						<button
							className="square-button button-type-1"
							type="submit"
							id="feedbackknap_send"
							disabled={ fields.feedback.length === 0 || fields.feedback.length > textareaMax }
						>
							{ formSendButtonText }
						</button>
					</>
				) }
			</Form>
		);
	};

	/**
	 * Renders the success message after feedback is submitted.
	 *
	 * @returns {JSX.Element|null} Success message UI or null
	 */
	const renderSuccess = () => {
		const { onSuccess, feedback: { successTitle, successContent } } = props;

		if (!onSuccess) {
			return null;
		}

		return (
			<div className="Feedback-Success">
				<SectionHeading
					className="Feedback-Heading"
					headline={ successTitle }
					text={ successContent }
				/>
			</div>
		);
	};

	const { isActive } = props;

	if (isActive !== 'True') {
		return null;
	}

	return (
		<div className="Feedback">
			{ renderQuestion() }
			{ renderForm() }
			{ renderSuccess() }
		</div>
	);
};

export default Feedback;